/**
 * Conversation Detailed Report — results table.
 *
 * Same chrome as the inbox and the Agent Performance table (row height, sticky
 * header, pinned-column shadow, colour tokens) so all three read as one
 * product. The difference is that the catalog is passed in rather than
 * imported: the tenant's additional properties are only known at runtime.
 */
import { useMemo, type JSX } from 'react'

import { readDetailCell, type AdditionalPropertyColumn } from './columns.js'
import type { ReportColumnDef } from '../agent-performance/columns.js'
import { ResizeHandle, useColumnResize } from '../../../hooks/useColumnResize.js'

interface ConversationDetailTableProps {
  rows: Record<string, unknown>[]
  columns: ReportColumnDef[]
  visibleColumns: string[]
  /** Rendered as a dimmed group header on the first additional-property column. */
  additionalColumns: AdditionalPropertyColumn[]
}

function Blank(): JSX.Element {
  return <span className="text-slate-300">—</span>
}

/** `9015` → `2h 30m`. Matches the export's formatting exactly. */
function formatDuration(seconds: unknown): string {
  const total = Number(seconds)
  if (!Number.isFinite(total) || total < 0) return ''
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = Math.round(total % 60)
  const parts: string[] = []
  if (h > 0) parts.push(`${h}h`)
  if (m > 0) parts.push(`${m}m`)
  if (s > 0 && h === 0) parts.push(`${s}s`)
  return parts.join(' ') || '0s'
}

/** Timestamps arrive pre-formatted from the API as `YYYY-MM-DD HH:MM` in the
 *  selected timezone (TO_CHAR in the SQL query), so they are split and
 *  displayed directly — no client-side Date parsing or timezone conversion. */
function formatDateTime(value: unknown): { date: string; time: string } | null {
  if (!value) return null
  const text = String(value).trim()
  if (!text) return null
  // Already formatted: "2026-09-27 13:50" → split on the space.
  const spaceIdx = text.indexOf(' ')
  if (spaceIdx > 0) {
    return { date: text.slice(0, spaceIdx), time: text.slice(spaceIdx + 1) }
  }
  // Fallback: a bare date or ISO string — parse and format.
  const d = new Date(text)
  if (Number.isNaN(d.getTime())) return null
  return {
    date: d.toISOString().slice(0, 10),
    time: d.toISOString().slice(11, 16),
  }
}

const DATE_KEYS = new Set([
  'openedAt', 'lastMessageAt', 'firstRespondedAt', 'resolvedAt', 'closedAt',
])
const DURATION_KEYS = new Set([
  'expectedResponseTimeSec', 'actualResponseTimeSec',
  'expectedResolutionTimeSec', 'actualResolutionTimeSec',
])

function cellFor(row: Record<string, unknown>, key: string): JSX.Element {
  const value = readDetailCell(row, key)
  if (value === null || value === undefined || value === '') return <Blank />

  if (key === 'referenceId') {
    return <span className="truncate font-mono text-[12px] font-semibold text-slate-900">{String(value)}</span>
  }
  if (DATE_KEYS.has(key)) {
    const parts = formatDateTime(value)
    if (!parts) return <Blank />
    // Date and time as separate spans: a column of timestamps is scanned
    // vertically, and dimming the time lets the date line up as the anchor.
    return (
      <span className="font-mono tabular-nums whitespace-nowrap">
        <span className="text-slate-700">{parts.date}</span>
        <span className="ml-1.5 text-slate-400">{parts.time}</span>
      </span>
    )
  }
  if (DURATION_KEYS.has(key)) {
    const text = formatDuration(value)
    return text ? <span className="font-mono tabular-nums">{text}</span> : <Blank />
  }
  if (key === 'slaBreached' || key === 'olaBreached') {
    const breached = value === true
    return (
      <span
        className={`inline-flex items-center rounded-[3px] px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset ${
          breached
            ? 'bg-red-50 text-red-700 ring-red-200'
            : 'bg-emerald-50 text-emerald-700 ring-emerald-200'
        }`}
      >
        {breached ? 'Breached' : 'Within'}
      </span>
    )
  }
  if (key === 'tags' && Array.isArray(value)) {
    return value.length === 0
      ? <Blank />
      : <span className="truncate text-slate-600">{value.join(', ')}</span>
  }
  const text = String(value).trim()
  if (!text) return <Blank />
  return <span className="truncate" title={text}>{text}</span>
}

export default function ConversationDetailTable({
  rows,
  columns,
  visibleColumns,
}: ConversationDetailTableProps): JSX.Element {
  const byKey = new Map(columns.map((c) => [c.key, c]))

  // Locked columns first (their stickyLeft offsets assume it), then the
  // user's order.
  const ordered: ReportColumnDef[] = [
    ...columns.filter((c) => c.locked && visibleColumns.includes(c.key)),
    ...visibleColumns
      .map((k) => byKey.get(k))
      .filter((c): c is ReportColumnDef => c !== undefined && !c.locked),
  ]

  // Resizable column widths — initialised from the catalog, reset when the
  // column set changes (e.g. additional properties load after the first run).
  const initialWidths = useMemo(() => ordered.map((c) => c.width), [ordered])
  const { columnWidths, handleResize } = useColumnResize(initialWidths)

  // Recompute stickyLeft offsets from the dynamic widths so pinned columns
  // stay flush as the user drags.
  let stickyAcc = 0
  const laidOut = ordered.map((c, i) => {
    const width = columnWidths[i] ?? c.width
    if (c.stickyLeft === undefined) return { ...c, width }
    const stickyLeft = stickyAcc
    stickyAcc += width
    return { ...c, width, stickyLeft }
  })
  const lastStickyKey = [...laidOut].reverse().find((c) => c.stickyLeft !== undefined)?.key

  return (
    <table
      className="w-full min-w-max border-separate border-spacing-0 text-[13px]"
      style={{ tableLayout: 'fixed' }}
    >
      <thead>
        <tr>
          {laidOut.map((col, i) => (
            <th
              key={col.key}
              style={{
                width: col.width,
                minWidth: col.width,
                ...(col.stickyLeft !== undefined ? { left: col.stickyLeft } : {}),
              }}
              className={`relative sticky top-0 h-11 border-b border-slate-200 bg-[#FAFBFC] px-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-slate-500 ${
                col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left'
              } ${col.stickyLeft !== undefined ? 'z-30' : 'z-20'} ${
                col.key === lastStickyKey ? 'shadow-[1px_0_0_0_rgb(226,232,240)]' : ''
              }`}
            >
              <span className="truncate">
                {col.key.startsWith('ap__') && (
                  <span className="mr-1 text-[9px] font-bold uppercase text-[#3366FF]/50">•</span>
                )}
                {col.label}
              </span>
              <ResizeHandle
                startWidth={col.width}
                onResize={(w) => handleResize(i, w)}
                onResizeEnd={(w) => handleResize(i, w)}
              />
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={String(row['id'] ?? i)} className="group">
            {laidOut.map((col) => (
              <td
                key={col.key}
                style={{
                  width: col.width,
                  minWidth: col.width,
                  maxWidth: col.width,
                  ...(col.stickyLeft !== undefined ? { left: col.stickyLeft } : {}),
                }}
                className={`h-[54px] border-b border-slate-100 bg-white px-4 align-middle text-slate-700 transition-colors group-hover:bg-[#F7F9FC] ${
                  col.stickyLeft !== undefined ? 'sticky z-10' : ''
                } ${col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : ''} ${
                  col.key === lastStickyKey ? 'shadow-[1px_0_0_0_rgb(241,245,249)]' : ''
                }`}
              >
                <div
                  className={
                    col.align === 'center'
                      ? 'flex items-center justify-center'
                      : col.align === 'right'
                        ? 'flex items-center justify-end'
                        : 'truncate'
                  }
                >
                  {cellFor(row, col.key)}
                </div>
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  )
}
